import { motion } from "framer-motion";
import { useContent } from "@/context/ContentContext";
import { Kicker, Reveal, SectionTitle } from "@/components/Reveal";
import PageHeader from "@/components/PageHeader";
import SplitText from "@/components/motion/SplitText";
import ImageReveal from "@/components/motion/ImageReveal";
import StackCards from "@/components/motion/StackCards";
import EditorialMarquee from "@/components/Marquee";
import JoinCta from "@/components/JoinCta";
import BoardSection from "@/components/BoardSection";

export default function About() {
  const c = useContent();
  const stages = c.pairs("path", "stages");

  return (
    <div data-testid="about-page">
      <PageHeader
        testid="about-header"
        kicker="About Veridian"
        title={c.get("hero", "kicker")}
        subtitle={c.get("hero", "subtitle")}
        image={c.get("vision_mission", "image", "/images/about.jpg")}
        imageAlt="Veridian campus"
      />

      {/* VISION / MISSION */}
      <section className="relative overflow-hidden bg-pine text-white" data-testid="vision-mission-section">
        <img
          src="/brand/mark.png"
          alt=""
          aria-hidden
          className="pointer-events-none absolute -right-24 top-1/2 h-[560px] w-[560px] -translate-y-1/2 object-contain opacity-[0.08]"
        />
        <div className="relative mx-auto grid max-w-[1200px] gap-6 px-5 py-20 md:grid-cols-2 md:py-28">
          <div className="rounded-[10px] bg-white/[0.07] p-8 md:p-10">
            <Kicker light>{c.get("vision_mission", "vision_label")}</Kicker>
            <SplitText
              as="p"
              inView
              text={c.get("vision_mission", "vision")}
              className="mt-4 font-display text-2xl font-medium leading-[1.3] text-white md:text-3xl"
              data-testid="vision-text"
            />
          </div>
          <div className="rounded-[10px] bg-ink/40 p-8 md:p-10">
            <Kicker light>{c.get("vision_mission", "mission_label")}</Kicker>
            <SplitText
              as="p"
              inView
              delay={0.2}
              text={c.get("vision_mission", "mission")}
              className="mt-4 font-display text-2xl font-medium leading-[1.3] text-white md:text-3xl"
              data-testid="mission-text"
            />
          </div>
        </div>
      </section>

      {/* COMMITMENTS — sticky stack */}
      <section className="bg-bone" data-testid="commitments-section">
        <div className="mx-auto max-w-[1200px] px-5 pt-20 md:pt-28">
          <Reveal className="max-w-3xl">
            <Kicker>{c.get("commitments", "kicker")}</Kicker>
            <SectionTitle>{c.get("commitments", "title")}</SectionTitle>
          </Reveal>
        </div>
        <div className="mt-10 md:mt-16">
          <StackCards items={c.pairs("commitments", "items")} />
        </div>
      </section>

      {/* PEOPLE */}
      <section className="bg-surface" data-testid="people-section">
        <div className="mx-auto max-w-[1200px] px-5 py-20 md:py-28">
          <Reveal className="max-w-3xl">
            <Kicker>{c.get("people", "kicker")}</Kicker>
            <SectionTitle>{c.get("people", "title")}</SectionTitle>
          </Reveal>
          <div className="mt-16 grid gap-12 md:grid-cols-12 md:gap-10">
            <div className="md:col-span-4">
              <ImageReveal
                src={c.get("people", "founder_image", "/images/founder.jpg")}
                alt={c.get("people", "founder_name")}
                className="aspect-[4/5] rounded-[10px]"
                direction="up"
                data-testid="founder-image"
              />
              <Reveal delay={0.2}>
                <h3 className="mt-5 font-display text-2xl font-semibold text-ink" data-testid="founder-name">
                  {c.get("people", "founder_name")}
                </h3>
                <p
                  className="mt-1.5 font-display text-xs font-semibold uppercase tracking-[0.16em] text-goldtext"
                  data-testid="founder-role"
                >
                  {c.get("people", "founder_role")}
                </p>
                <p className="mt-3 leading-relaxed text-body" data-testid="founder-bio">
                  {c.get("people", "founder_bio")}
                </p>
              </Reveal>
            </div>
            <BoardSection title={c.get("people", "board_title")} text={c.get("people", "board_text")} />
          </div>
        </div>
      </section>

      <EditorialMarquee />

      {/* PATH */}
      <section className="bg-bone" data-testid="path-section">
        <div className="mx-auto max-w-[1200px] px-5 py-20 md:py-28">
          <Reveal className="max-w-3xl">
            <Kicker>{c.get("path", "kicker")}</Kicker>
            <SectionTitle>{c.get("path", "title")}</SectionTitle>
          </Reveal>
          <div className="mt-12 grid items-start gap-5 md:grid-cols-3">
            {stages.map(([stage, place, desc, img], i) => (
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.9, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                className={`group relative self-start overflow-hidden ${i === 1 ? "md:mt-16" : i === 2 ? "md:mt-32" : ""}`}
                data-testid={`path-stage-${i}`}
                data-cursor="view"
              >
                <div className="relative aspect-[4/5] overflow-hidden rounded-[10px]">
                  <img
                    src={img}
                    alt={place}
                    loading="lazy"
                    className="img-editorial h-full w-full object-cover transition-transform duration-[1400ms] group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/20 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-7 text-white">
                    <p className="font-display text-xs font-semibold uppercase tracking-[0.16em] text-terra">{stage}</p>
                    <h3 className="mt-2 font-display text-3xl font-semibold !text-white md:text-4xl">{place}</h3>
                    <p className="mt-2 max-w-xs leading-relaxed text-[#CFE3D9]">{desc}</p>
                  </div>
                  <span className="absolute right-5 top-5 font-display text-xs font-semibold text-white/70">
                    0{i + 1} — 03
                  </span>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <JoinCta />
    </div>
  );
}
